import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import { marketing } from 'config/global';
import { CtaButton } from './components/CtaButton';

const AVATARS = [
  'linear-gradient(135deg,#7fa8ff,#b69bff)',
  'linear-gradient(135deg,#6ed7a6,#0b7b50)',
  'linear-gradient(135deg,#f5a3c2,#c53d56)',
  'linear-gradient(135deg,#FDE47A,#d97706)',
];

const DeviceIcon = () => (
  <svg viewBox="0 0 24 24" className={styles.deviceGlyph} aria-hidden="true">
    <rect x="3" y="4" width="13" height="10" rx="1.5" />
    <rect x="16" y="7" width="5" height="13" rx="1.5" />
    <path d="M3 14h13M7 18h5" />
  </svg>
);

// Memory-keeping use case — final CTA.
export const Section35 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.glow} aria-hidden="true" />
      <div className={styles.wrap}>
        <h2 className={styles.title}>
          {t.rich('pages.useCases.memoryKeeping.finalCta.title', {
            highlight: (chunks) => (
              <span className={styles.highlight}>{chunks}</span>
            ),
          })}
        </h2>
        <p className={styles.sub}>
          {t.rich('pages.useCases.memoryKeeping.finalCta.sub', {
            br: () => <br />,
          })}
        </p>

        <div className={styles.social}>
          <div className={styles.avatars}>
            {AVATARS.map((bg, i) => (
              <span
                key={i}
                className={styles.avatar}
                style={{ background: bg }}
              />
            ))}
          </div>
          <span>
            {t.rich('pages.useCases.memoryKeeping.finalCta.social', {
              strong: (chunks) => (
                <strong className={styles.count}>{chunks}</strong>
              ),
            })}
          </span>
        </div>

        <div className={styles.cta}>
          <CtaButton
            variant="primary"
            href={appRoutes.LOGIN}
            reportLocation="Memory Keeping Final CTA"
            withArrow
          >
            {t('pages.useCases.memoryKeeping.finalCta.ctaSave')}
          </CtaButton>
          <a
            href={marketing.appStoreUri}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.store}
          >
            <DeviceIcon />
            App Store
          </a>
          <a
            href={marketing.googlePlayUri}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.store}
          >
            <DeviceIcon />
            Google Play
          </a>
        </div>

        <p className={styles.note}>
          {t('pages.useCases.memoryKeeping.finalCta.note')}
        </p>
      </div>
    </section>
  );
};

const styles = {
  section:
    'relative overflow-hidden marketing-dark:bg-mf-navy pt-24 pb-[80px] text-center',
  glow: classNames(
    'pointer-events-none absolute left-1/2 bottom-[-200px] h-[600px] w-[1000px] max-w-none -translate-x-1/2',
    'bg-[radial-gradient(circle_at_42%_100%,rgba(189,222,255,0.55),transparent_65%),radial-gradient(circle_at_66%_100%,rgba(189,222,255,0.35),transparent_60%)]',
    'marketing-dark:bg-[radial-gradient(circle_at_50%_100%,rgba(40,80,200,0.4),transparent_65%)]',
  ),
  wrap: 'relative z-[2] mx-auto max-w-[1200px] px-6 lg:px-10',
  title:
    'mx-auto max-w-[720px] font-inter text-[34px] font-extrabold leading-[1.08] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white lg:text-[56px]',
  highlight: 'text-mf-blue marketing-dark:text-mf-coral',
  sub: 'mx-auto mt-5 max-w-[540px] text-lg leading-[1.55] text-mf-gray-600 marketing-dark:text-white/70 lg:text-[19px]',
  social:
    'mt-5 flex flex-wrap items-center justify-center gap-2 text-sm text-mf-gray-500 marketing-dark:text-white/50',
  avatars: 'flex -space-x-2',
  // Overlap ring matches the page background in each theme.
  avatar:
    'h-7 w-7 rounded-full border-2 border-white marketing-dark:border-mf-navy',
  count: 'font-bold text-mf-off-black-600 marketing-dark:text-white/85',
  cta: 'mt-8 flex flex-wrap justify-center gap-[13px]',
  store: classNames(
    'inline-flex items-center gap-2 rounded-full border px-8 py-4 text-[17px] font-bold transition-colors',
    'border-mf-gray-300 bg-white text-mf-off-black-600 hover:border-mf-gray-500',
    'marketing-dark:border-white/30 marketing-dark:bg-transparent marketing-dark:text-white marketing-dark:hover:border-white',
  ),
  deviceGlyph: 'h-4 w-4 fill-none stroke-current stroke-2',
  note: 'mt-6 text-sm text-mf-gray-500 marketing-dark:text-white/50',
};
